import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  serverTimestamp, 
  collection,
  query,
  where,
  getDocs
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './config';
import { AffiliateProfile } from '../../types/affiliate';

export const AFFILIATES_COLLECTION = 'affiliates';

/**
 * Get affiliate profile by user ID
 */
export const getAffiliateProfile = async (userId: string): Promise<AffiliateProfile | null> => {
  try {
    // First try to get by document ID (assuming docId == userId)
    const docRef = doc(db, AFFILIATES_COLLECTION, userId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() } as AffiliateProfile;
    }
    
    // Fallback: query by userId field if docId != userId
    const q = query(collection(db, AFFILIATES_COLLECTION), where('userId', '==', userId));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      return { id: doc.id, ...doc.data() } as AffiliateProfile;
    }

    return null;
  } catch (error: any) {
    // Suppress permission errors for non-affiliates/non-admins
    if (error.code === 'permission-denied') {
      // Expected behavior for non-affiliates, no need to log
      return null;
    }
    console.error('Error fetching affiliate profile:', error);
    throw error;
  }
};

/**
 * Create a new affiliate profile
 */
export const createAffiliateProfile = async (
  userId: string, 
  referralCode: string
): Promise<void> => {
  try {
    const affiliateData: Omit<AffiliateProfile, 'id'> = {
      userId,
      referralCode,
      status: 'pending', // Default to pending until approved or auto-approved
      stats: {
        totalClicks: 0,
        totalSignups: 0,
        totalEarnings: 0,
        pendingEarnings: 0,
        lastPaymentDate: null,
        nextPaymentDate: null,
      },
      createdAt: serverTimestamp() as any,
      updatedAt: serverTimestamp() as any,
    };

    // Use userId as the document ID for easier lookup
    await setDoc(doc(db, AFFILIATES_COLLECTION, userId), affiliateData);
  } catch (error) {
    console.error('Error creating affiliate profile:', error);
    throw error;
  }
};

/**
 * Check if a referral code is valid and return the affiliate ID
 */
export const validateReferralCode = async (code: string): Promise<string | null> => {
  try {
    const q = query(
      collection(db, AFFILIATES_COLLECTION), 
      where('referralCode', '==', code),
      where('status', '==', 'active')
    );
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      return querySnapshot.docs[0].data().userId; // Return the affiliate's user ID
    }
    return null;
  } catch (error) {
    console.error('Error validating referral code:', error);
    return null;
  }
};

/**
 * Update Stripe Connect Account ID
 */
export const updateAffiliateStripeId = async (userId: string, stripeAccountId: string): Promise<void> => {
  try {
    const docRef = doc(db, AFFILIATES_COLLECTION, userId);
    await updateDoc(docRef, {
      stripeAccountId,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating affiliate Stripe ID:', error);
    throw error;
  }
};

/**
 * Get Stripe Connect Onboarding Link
 */
export const getStripeOnboardingLink = async (returnUrl: string, refreshUrl: string): Promise<string> => {
  try {
    // First ensure account exists
    const createAccountFn = httpsCallable(functions, 'createStripeConnectAccount');
    await createAccountFn();

    // Then get link
    const getLinkFn = httpsCallable(functions, 'getStripeConnectOnboardingLink');
    const result = await getLinkFn({ returnUrl, refreshUrl });
    return (result.data as any).url;
  } catch (error) {
    console.error('Error getting Stripe onboarding link:', error);
    throw error;
  }
};

/**
 * Get users referred by an affiliate code
 * Now queries the 'referrals' subcollection of the affiliate document
 */
export const getAffiliateReferrals = async (affiliateId: string): Promise<any[]> => {
  try {
    const referralsRef = collection(db, AFFILIATES_COLLECTION, affiliateId, 'referrals');
    // Order by createdAt desc
    // Note: This might require a composite index if we add more filters
    const q = query(referralsRef); 
    
    const snapshot = await getDocs(q);
    
    const referrals = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : new Date()
    }));
    
    // Sort in memory to avoid index issues for now
    return referrals.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  } catch (error) {
    console.error('Error fetching referrals:', error);
    return [];
  }
};
